import { useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { FileText, LogIn, Map as MapIcon, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";

const SHOW_ON = [/^\/$/, /^\/proposals$/, /^\/proposal\//, /^\/dashboard/, /^\/(about|guidelines|privacy|contact|report)$/];

const tabStyle = (active) => ({
  flex: 1, minHeight: 60, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, textDecoration: "none",
  color: active ? "#C8102E" : "#3b4b52", border: "none", background: "transparent", cursor: "pointer", font: `${active ? 700 : 500} 12px Inter, sans-serif`,
});

const Tab = ({ to, end, label, Icon, id }) => (
  <NavLink to={to} end={end} data-testid={`dash-tab-${id}`} style={({ isActive }) => tabStyle(isActive)}>
    {({ isActive }) => <><Icon size={22} strokeWidth={isActive ? 2.4 : 2} />{label}</>}
  </NavLink>
);

const NewTab = ({ onClick }) => (
  <button type="button" onClick={onClick} aria-label="Start a proposal" data-testid="dash-tab-new" style={{ ...tabStyle(false), color: "#C8102E", font: "700 12px Inter, sans-serif" }}>
    <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: "50%", background: "#C8102E", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      marginTop: -18, boxShadow: "0 6px 16px rgba(200,16,46,.35)", border: "3px solid #fff" }}>
      <Plus size={24} strokeWidth={2.6} />
    </span>
    Propose
  </button>
);

/** Every phone visitor gets a fixed bottom bar (Explore · + · My proposals / Sign in); the + sends signed-out people to sign in. */
export const MobileBar = () => {
  const { user } = useAuth();
  const mobile = useIsMobile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shown = mobile && SHOW_ON.some((re) => re.test(pathname));

  useEffect(() => {
    document.body.classList.toggle("has-mobile-bar", shown);
    return () => document.body.classList.remove("has-mobile-bar");
  }, [shown]);

  if (!shown) return null;
  return (
    <nav aria-label="Quick links" data-testid="dash-bottom-bar"
      style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", background: "#fff", borderTop: "1px solid var(--border)",
        paddingBottom: "env(safe-area-inset-bottom)", boxShadow: "0 -6px 20px rgba(20,35,42,.06)" }}>
      <Tab to="/?view=map" end label="Explore" Icon={MapIcon} id="explore" />
      <NewTab onClick={() => navigate(user ? "/new" : "/signin?next=%2Fnew")} />
      {user
        ? <Tab to="/dashboard" end label="My proposals" Icon={FileText} id="mine" />
        : <Tab to="/signin" label="Sign in" Icon={LogIn} id="signin" />}
    </nav>
  );
};
